import { ExistsQuery } from 'client/modules/Surveys/components/searchkit/ExistsFieldFilter/ExistsFieldFilterAccessor';
import { searchResults as searchResultsAction } from 'client/modules/Surveys/SurveyActions';
import { getShouldReloadQuery } from 'client/modules/Surveys/SurveyReducer';
import useActions from 'client/util/useActions';
import useCreateHistory from 'client/util/useCreateHistory';
import { useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { AxiosESTransport, BoolMust, SearchkitManager } from 'searchkit';

const useSearchkit = survey => {
  const surveyId = survey?.hasResponses ? survey.id : 'demo';
  const host = useMemo(
    () => (survey ? `/api/surveys/${surveyId}/responses` : null),
    [survey, surveyId],
  );

  const createHistory = useCreateHistory();
  const searchResults = useActions(searchResultsAction);
  const shouldReload = useSelector(getShouldReloadQuery);

  const reloadingTimerRef = useRef(null);

  const transportOptions = useMemo(
    () => ({
      searchUrlPath: '/search',
      timeout: 10000,
      createHistory,
      httpHeaders: {
        'x-org': survey?.organization,
      },
    }),
    [createHistory, survey?.organization],
  );

  const searchkit = useMemo(() => {
    if (!host) {
      return null;
    }
    return new SearchkitManager(host, transportOptions);
  }, [host, transportOptions]);

  useEffect(() => {
    if (!searchkit || !host) {
      return undefined;
    }
    if (searchkit.transport.host !== host) {
      searchkit.transport = new AxiosESTransport(host, transportOptions);
      searchkit.reloadSearch();
    }
    return undefined;
  }, [host, searchkit, transportOptions]);

  useEffect(() => {
    if (!searchkit) {
      return undefined;
    }
    const defaultQueryAccessor = searchkit.addDefaultQuery(query => {
      return query.addQuery(BoolMust([ExistsQuery('score')]));
    });
    searchkit.setQueryProcessor(plainQueryObject => {
      const demo = !survey || !survey.hasResponses;
      return {
        query: plainQueryObject,
        demo,
      };
    });
    const removeListener = searchkit.addResultsListener(results => {
      searchResults(results.hits.hits, results.hits.total);
    });
    return () => {
      removeListener();
      searchkit.removeAccessor(defaultQueryAccessor);
    };
  }, [searchkit, searchResults, survey]);

  useEffect(() => {
    if (!searchkit || !shouldReload) {
      return undefined;
    }
    if (!reloadingTimerRef.current) {
      reloadingTimerRef.current = setTimeout(() => {
        searchkit.reloadSearch();
        setTimeout(() => {
          reloadingTimerRef.current = null;
        }, 1000);
      }, 1000);
    }
    return () => {
      if (reloadingTimerRef.current) {
        clearTimeout(reloadingTimerRef.current);
        reloadingTimerRef.current = null;
      }
    };
  }, [shouldReload, searchkit]);

  return searchkit;
};

export default useSearchkit;
